import { extractTemplateVariables } from './mustacheTemplate';

/**
 * Custom outbound-header templating for webhooks — shared validation core.
 *
 * A webhook's `headerTemplate` is a `{ [headerName]: mustacheValueTemplate }`
 * map. Each value is rendered per delivery against a small, event-agnostic
 * context ({@link HEADER_ALLOWED_VARIABLES}).
 *
 * The validator is entry-based (rather than record-based) so callers that
 * track row order — the settings form — can map a problem back to the input
 * it came from, and so duplicate names (impossible to detect once collapsed
 * into a map) are still caught.
 */

// Variables a header value template may reference. Flat and event-agnostic
// (unlike the payload allow-list). `token` is the per-webhook outbound auth
// token (`webhookConfig.token`); it is encrypted at rest and only substituted
// at delivery time by the worker, so the plaintext never rides the delivery
// message.
export const HEADER_ALLOWED_VARIABLES: ReadonlySet<string> = new Set([
  'timestamp',
  'user_id',
  'token',
]);

// Valid HTTP header field-name characters (RFC 7230 "token").
const HEADER_NAME_RE = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

// System-set headers a custom header must not become shadowed.
const RESERVED_HEADER_NAMES: ReadonlySet<string> = new Set(['x-ipf-signature']);

export type HeaderTemplateProblem = { index: number; name: string } & (
  | { field: 'name'; code: 'invalidName' | 'duplicateName' | 'reservedName' }
  | { field: 'value'; code: 'newline' | 'invalidTemplate' }
  | { field: 'value'; code: 'unsupportedVariable'; variable: string }
);

// Validate one header entry, appending any problems found.
function validateHeaderEntry(
  index: number,
  name: string,
  value: string,
  seen: Set<string>,
  problems: HeaderTemplateProblem[],
): void {
  const lower = name.toLowerCase();
  if (!HEADER_NAME_RE.test(name)) {
    problems.push({ index, name, field: 'name', code: 'invalidName' });
  }
  if (seen.has(lower)) {
    problems.push({ index, name, field: 'name', code: 'duplicateName' });
  }
  seen.add(lower);
  if (RESERVED_HEADER_NAMES.has(lower)) {
    problems.push({ index, name, field: 'name', code: 'reservedName' });
  }
  if (/[\r\n]/.test(value)) {
    problems.push({ index, name, field: 'value', code: 'newline' });
  }

  let variables: string[];
  try {
    variables = extractTemplateVariables(value);
  } catch {
    problems.push({ index, name, field: 'value', code: 'invalidTemplate' });
    return;
  }
  for (const variable of variables) {
    if (!HEADER_ALLOWED_VARIABLES.has(variable)) {
      problems.push({
        index,
        name,
        field: 'value',
        code: 'unsupportedVariable',
        variable,
      });
    }
  }
}

/**
 * Validate an ordered list of header entries. Returns the list of problems
 * found (empty = valid), each tagged with the entry's `index` so a caller that
 * tracks row order (e.g. a settings form) can attach it to the right input.
 */
export function validateHeaderTemplateEntries(
  entries: readonly (readonly [name: string, value: string])[],
): HeaderTemplateProblem[] {
  const problems: HeaderTemplateProblem[] = [];
  const seen = new Set<string>();
  entries.forEach(([name, value], index) => {
    validateHeaderEntry(index, name, value, seen, problems);
  });
  return problems;
}

/** Render a {@link HeaderTemplateProblem} as the API's plain-English message. */
export function formatHeaderTemplateProblem(
  problem: HeaderTemplateProblem,
): string {
  switch (problem.code) {
    case 'invalidName':
      return `invalid header name: "${problem.name}"`;
    case 'duplicateName':
      return `duplicate header name: "${problem.name}"`;
    case 'reservedName':
      return `reserved header name: "${problem.name}"`;
    case 'newline':
      return `header "${problem.name}" value must not contain newlines`;
    case 'invalidTemplate':
      return `header "${problem.name}" has an invalid template`;
    case 'unsupportedVariable':
      return `header "${problem.name}" references unsupported variable "${problem.variable}"`;
  }
}

/**
 * Validate a header-template map. Returns a list of human-readable problems
 * (empty = valid): bad/duplicate/reserved names, newlines, or value templates
 * that reference unsupported variables / fail to parse.
 */
export function validateHeaderTemplates(
  headers: Record<string, string>,
): string[] {
  return validateHeaderTemplateEntries(Object.entries(headers)).map(
    formatHeaderTemplateProblem,
  );
}
